/**
 * SettingsPrimitives — piezas compartidas por las 12 secciones de Settings V2.
 *
 * Existen para que todas las secciones respiren igual: mismo ritmo vertical,
 * mismas etiquetas, mismos controles. Si una sección necesita algo distinto,
 * casi siempre es señal de que falta una primitiva aquí, no de que esa sección
 * deba inventarse su propio estilo.
 *
 * Todo es visual: ninguna primitiva llama a la API.
 */
import React, { useId, useState } from "react";
import { CaretDown, Check, Info, Lock, PencilSimple } from "@phosphor-icons/react";
import Chip from "@/components/ui-dv/Chip";
import { USED_IN } from "@/data/settingsV2Demo";

/* -------------------------------------------------------------------------
 * Bloques
 * ---------------------------------------------------------------------- */

/** Bloque principal de una sección: título serif, subtítulo y contenido. */
export function Block({ title, description, action, children, testId }) {
  return (
    <section className="mb-8" data-testid={testId}>
      <div className="flex items-end justify-between gap-4 mb-3 flex-wrap">
        <div className="min-w-0">
          <h3 className="font-serif-dv text-xl text-stone-900 dark:text-stone-100">{title}</h3>
          {description && (
            <p className="text-sm text-stone-500 dark:text-stone-400 mt-0.5 max-w-2xl">
              {description}
            </p>
          )}
        </div>
        {action}
      </div>
      {children}
    </section>
  );
}

/** Tarjeta neutra. `padded={false}` para listas y tablas a sangre. */
export function Card({ children, className = "", padded = true, testId }) {
  return (
    <div
      className={`dv-card ${padded ? "" : "p-0 overflow-hidden"} ${className}`}
      data-testid={testId}
    >
      {children}
    </div>
  );
}

/** Rejilla de campos: una columna en móvil, dos en escritorio. */
export function FieldGrid({ children, cols = 2 }) {
  const md = cols === 3 ? "md:grid-cols-3" : "md:grid-cols-2";
  return <div className={`grid grid-cols-1 ${md} gap-x-6 gap-y-5`}>{children}</div>;
}

/* -------------------------------------------------------------------------
 * Campos
 * ---------------------------------------------------------------------- */

function Label({ htmlFor, children, hint, approved, usedIn }) {
  return (
    <div className="flex items-center gap-2 mb-1.5 flex-wrap">
      <label
        htmlFor={htmlFor}
        className="text-[11px] tracking-[0.18em] uppercase text-stone-500 dark:text-stone-400"
      >
        {children}
      </label>
      {approved && <ApprovedMark approved={approved} />}
      {usedIn && <UsedIn topic={usedIn} />}
      {hint && (
        <span className="text-[11px] text-stone-400 dark:text-stone-500 normal-case tracking-normal">
          {hint}
        </span>
      )}
    </div>
  );
}

/** Campo de texto / número. */
export function Field({
  label, value, onChange, type = "text", placeholder, hint, approved,
  usedIn, suffix, disabled, testId, span,
}) {
  const id = useId();
  return (
    <div className={span === 2 ? "md:col-span-2" : ""}>
      <Label htmlFor={id} hint={hint} approved={approved} usedIn={usedIn}>{label}</Label>
      <div className="relative">
        <input
          id={id}
          type={type}
          value={value ?? ""}
          disabled={disabled}
          placeholder={placeholder}
          onChange={(e) => onChange?.(type === "number" ? Number(e.target.value) : e.target.value)}
          data-testid={testId}
          className={`dv-input w-full ${suffix ? "pr-12" : ""} ${disabled ? "opacity-60" : ""}`}
        />
        {suffix && (
          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-stone-400 pointer-events-none">
            {suffix}
          </span>
        )}
      </div>
    </div>
  );
}

/** Área de texto. */
export function TextArea({ label, value, onChange, rows = 3, hint, usedIn, testId, span = 2 }) {
  const id = useId();
  return (
    <div className={span === 2 ? "md:col-span-2" : ""}>
      <Label htmlFor={id} hint={hint} usedIn={usedIn}>{label}</Label>
      <textarea
        id={id}
        rows={rows}
        value={value ?? ""}
        onChange={(e) => onChange?.(e.target.value)}
        data-testid={testId}
        className="dv-input w-full resize-y"
      />
    </div>
  );
}

/** Desplegable. `options` = [{ value, label }]. */
export function Select({ label, value, onChange, options, hint, approved, usedIn, testId, span }) {
  const id = useId();
  return (
    <div className={span === 2 ? "md:col-span-2" : ""}>
      <Label htmlFor={id} hint={hint} approved={approved} usedIn={usedIn}>{label}</Label>
      <select
        id={id}
        value={value ?? ""}
        onChange={(e) => onChange?.(e.target.value)}
        data-testid={testId}
        className="dv-input w-full"
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
    </div>
  );
}

/**
 * Interruptor con explicación. El área pulsable cumple los 44px de alto.
 *
 * Las marcas ("Aprobado", "Usado en N sitios") van FUERA del botón: `UsedIn`
 * es a su vez pulsable y anidar un botón dentro de otro es HTML inválido.
 */
export function Toggle({ label, description, checked, onChange, approved, usedIn, disabled, testId }) {
  return (
    <div className="py-1">
      <button
        type="button"
        role="switch"
        aria-checked={!!checked}
        aria-label={label}
        disabled={disabled}
        onClick={() => onChange?.(!checked)}
        data-testid={testId}
        className={`w-full text-left flex items-start gap-3 min-h-[44px] py-1 ${
          disabled ? "opacity-60 cursor-not-allowed" : ""
        }`}
      >
        <span
          className={`mt-0.5 shrink-0 w-9 h-5 rounded-full transition-colors relative ${
            checked
              ? "bg-stone-900 dark:bg-stone-100"
              : "bg-stone-300 dark:bg-stone-700"
          }`}
        >
          <span
            className={`absolute top-0.5 w-4 h-4 rounded-full bg-white dark:bg-stone-900 transition-all ${
              checked ? "left-[1.125rem]" : "left-0.5"
            }`}
          />
        </span>
        <span className="min-w-0">
          <span className="block text-sm text-stone-900 dark:text-stone-100">{label}</span>
          {description && (
            <span className="block text-xs text-stone-500 dark:text-stone-400 mt-0.5">
              {description}
            </span>
          )}
        </span>
      </button>
      {(approved || usedIn) && (
        <div className="pl-12 flex items-center gap-2 flex-wrap">
          {approved && <ApprovedMark approved={approved} />}
          {usedIn && <UsedIn topic={usedIn} />}
        </div>
      )}
    </div>
  );
}

/** Casilla compacta para listas de requisitos. */
export function CheckRow({ label, checked, onChange, description, testId }) {
  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={!!checked}
      aria-label={label}
      onClick={() => onChange?.(!checked)}
      data-testid={testId}
      className="w-full text-left flex items-center gap-3 min-h-[44px] px-1 rounded-md hover:bg-stone-50 dark:hover:bg-stone-900 transition-colors"
    >
      <span
        className={`shrink-0 w-5 h-5 rounded border flex items-center justify-center transition-colors ${
          checked
            ? "bg-stone-900 border-stone-900 text-white dark:bg-stone-100 dark:border-stone-100 dark:text-stone-900"
            : "border-stone-300 dark:border-stone-700"
        }`}
      >
        {checked && <Check size={12} weight="bold" />}
      </span>
      <span className="min-w-0">
        <span className="block text-sm text-stone-900 dark:text-stone-100">{label}</span>
        {description && (
          <span className="block text-xs text-stone-500 dark:text-stone-400">{description}</span>
        )}
      </span>
    </button>
  );
}

/** Fila etiqueta → valor para datos que en la vista previa no se editan. */
export function ReadRow({ label, value, hint, testId }) {
  return (
    <div className="flex items-baseline justify-between gap-4 py-2.5 border-b border-stone-100 dark:border-stone-800 last:border-0" data-testid={testId}>
      <div className="min-w-0">
        <div className="text-sm text-stone-700 dark:text-stone-300">{label}</div>
        {hint && <div className="text-xs text-stone-400 dark:text-stone-500 mt-0.5">{hint}</div>}
      </div>
      <div className="text-sm text-stone-900 dark:text-stone-100 tabular-nums shrink-0">{value}</div>
    </div>
  );
}

/* -------------------------------------------------------------------------
 * Señales
 * ---------------------------------------------------------------------- */

/**
 * Marca los valores que vienen de una decisión de producto ya aprobada.
 *
 * La marca sigue al VALOR, no a la etiqueta: si en la vista previa se cambia
 * un número aprobado, deja de decir "Aprobado" y pasa a decir que se ha
 * modificado. Sin esto, la pantalla podría enseñar un candado de "aprobado"
 * junto a una cifra que no ha aprobado nadie.
 *
 * `approved` acepta `true` (aprobado y sin tocar) o un objeto
 * `{ baseline, value }` para comprobar si sigue coincidiendo.
 */
export function ApprovedMark({ approved = true }) {
  const changed = approved
    && typeof approved === "object"
    && String(approved.value) !== String(approved.baseline);

  if (changed) {
    return (
      <span
        className="inline-flex items-center gap-1 text-[10px] text-amber-700 dark:text-amber-300 normal-case tracking-normal"
        title={`Modificado en la vista previa · valor aprobado: ${approved.baseline}`}
        data-testid="approved-mark-changed"
      >
        <PencilSimple size={10} weight="fill" />
        Modificado · aprobado {String(approved.baseline)}
      </span>
    );
  }

  return (
    <span
      className="inline-flex items-center gap-1 text-[10px] text-stone-500 dark:text-stone-400 normal-case tracking-normal"
      title="Valor procedente de una decisión de producto aprobada"
      data-testid="approved-mark"
    >
      <Lock size={10} weight="fill" />
      Aprobado
    </span>
  );
}

/**
 * "Configurar una vez" — enseña que un dato alimenta varias superficies.
 * Discreto por defecto; el detalle solo aparece si se pulsa.
 */
export function UsedIn({ topic }) {
  const [open, setOpen] = useState(false);
  const places = USED_IN[topic];
  if (!places || places.length === 0) return null;
  return (
    <span className="inline-flex items-center normal-case tracking-normal">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid={`used-in-${topic}`}
        aria-expanded={open}
        className="text-[10px] text-stone-400 hover:text-stone-600 dark:hover:text-stone-200 underline decoration-dotted underline-offset-2 min-h-[44px] inline-flex items-center px-1 -mx-1"
      >
        Usado en {places.length} sitios
      </button>
      {open && (
        <span
          className="ml-2 text-[10px] text-stone-500 dark:text-stone-400"
          data-testid={`used-in-${topic}-detail`}
        >
          {places.join(" · ")}
        </span>
      )}
    </span>
  );
}

/** Nota explicativa sobria. Sin iconos gritones ni fondos de color fuerte. */
export function Note({ children, testId }) {
  return (
    <div
      className="flex items-start gap-2 text-xs text-stone-500 dark:text-stone-400 mb-4"
      data-testid={testId}
    >
      <Info size={14} className="mt-0.5 shrink-0 text-stone-400" />
      <p className="max-w-2xl">{children}</p>
    </div>
  );
}

/** Contenedor plegable para ajustes avanzados (divulgación progresiva). */
export function Advanced({ label = "Configuración avanzada", children, testId }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="mt-3" data-testid={testId}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        data-testid={`${testId}-toggle`}
        className="inline-flex items-center gap-1.5 text-xs text-stone-500 hover:text-stone-900 dark:hover:text-stone-100 min-h-[44px]"
      >
        <CaretDown size={12} weight="bold" className={`transition-transform ${open ? "rotate-180" : ""}`} />
        {label}
      </button>
      {open && (
        <div className="mt-2 pt-4 border-t border-stone-100 dark:border-stone-800" data-testid={`${testId}-body`}>
          {children}
        </div>
      )}
    </div>
  );
}

/** Estado de conexión / disponibilidad, con el tono de Chip ya resuelto. */
export function StatusChip({ tone = "stone", children, testId }) {
  return (
    <Chip tone={tone} size="xs" dot data-testid={testId}>{children}</Chip>
  );
}

/** Etiqueta para lo que todavía no existe y no debe parecer que existe. */
export function FutureChip({ children = "Futuro" }) {
  return <Chip tone="stone" size="xs">{children}</Chip>;
}
